/**
 * REST API adapters for SideBar component
 * Transforms REST responses to match GraphQL structure
 *
 * Usage: Replace import:
 * - import { useOffersCountLazyQuery } from './queries';
 * + import { useOffersCountLazyQuery } from './queries.rest';
 */
import { useCallback, useMemo, useState } from 'react';

import { useMyOffers } from '@hooks/queries';

import type { UserOffersCountQuery } from './gqlTypes/UserOffersCountQuery';

/**
 * REST adapter for useOffersCountLazyQuery
 * Matches Apollo useLazyQuery interface
 */
export const useOffersCountLazyQuery = () => {
  const [shouldFetch, setShouldFetch] = useState(false);

  // Fetch active offers count
  const {
    data: activeData,
    isLoading: activeLoading,
    refetch: refetchActive,
  } = useMyOffers({
    is_published: true,
    is_expired: false,
    page_size: 1,
  });

  // Fetch expired offers count
  const {
    data: expiredData,
    isLoading: expiredLoading,
    refetch: refetchExpired,
  } = useMyOffers({
    is_expired: true,
    page_size: 1,
  });

  // Transform to GraphQL format
  const transformedData: UserOffersCountQuery | undefined = useMemo(() => {
    if (!shouldFetch || !activeData || !expiredData) return undefined;

    return {
      me: {
        __typename: 'User' as const,
        activeOffers: activeData.count,
        expiredOffers: expiredData.count,
        totalApplications: 0, // TODO: Add separate endpoint for total applications
      },
    };
  }, [shouldFetch, activeData, expiredData]);

  // Lazy fetch function
  const fetchData = useCallback(async () => {
    setShouldFetch(true);
    await Promise.all([refetchActive(), refetchExpired()]);
  }, [refetchActive, refetchExpired]);

  const loading = shouldFetch && (activeLoading || expiredLoading);

  return [
    fetchData,
    {
      data: transformedData,
      loading,
      error: undefined,
      called: shouldFetch,
    },
  ] as const;
};
